import { createTheme, ThemeProvider } from '@mui/material'
import {
  DataGrid,
  enUS,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridRowId,
  type GridRowSelectionModel,
} from '@mui/x-data-grid'
import type React from 'react'
import { useContext, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import type { AdministratorInfo } from '../interfaces/User'
import { LanguageContext } from '../util/LanguageContext'

type UserListProps = {
  items: AdministratorInfo[]
  handleSelectionModelChange: (selectionModel: GridRowSelectionModel) => void
  gridSelection: GridRowId[]
}

const ragistrationDateSortComparator = (
  v1: string,
  v2: string,
  cellParams1: any,
  cellParams2: any,
) => {
  const row1 = cellParams1.api.getRow(cellParams1.id)
  const row2 = cellParams2.api.getRow(cellParams2.id)

  if (v1 == null && v2 == null) return 0;
  if (v1 == null) return 1;
  if (v2 == null) return -1;

  if (row1?.registration_date && row2?.registration_date) {
    // 対象カラムでソートしてからregistration_dateでソートする
    return v1.localeCompare(v2) || row1.registration_date.localeCompare(row2.registration_date)
  }
  return 0
}

export const UserList: React.FC<UserListProps> = props => {
  const auth = useAuth()
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    rhq: auth.role === 0 || auth.role === 2,
    sales_company: auth.role === 2 || auth.role === 3,
    email: auth.role !== 0,
    role: auth.role !== 0,
  })

  // 文字列変換用
  const translation = useContext(LanguageContext)

  //時刻変換
  const conversionTime = (time: string): string => {
    const utcDate = new Date(time)
    const localDate = utcDate.toLocaleString()
    return localDate.slice(0, -3)
  }

  const columns: GridColDef[] = [
    {
      field: 'rhq',
      headerName: translation['label_RHQ_info'],
      editable: false,
      flex: 1,
      renderCell: params => {
        return <div>{params.value}</div>
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
      maxWidth: 70,
    },
    {
      field: 'sales_company',
      headerName: translation['label_SalesCompany_info'],
      editable: false,
      flex: 1,
      renderCell: params => {
        return <div>{params.value}</div>
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'login_id',
      headerName: 'ID',
      editable: false,
      flex: 1,
      renderCell: params => {
        return <div>{params.value}</div>
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'password',
      headerName: translation['label_password'],
      editable: false,
      flex: 1,
      renderCell: params => {
        return <div>{params.value}</div>
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'email',
      headerName: translation['label_email'],
      editable: false,
      flex: 2,
      renderCell: params => {
        const splitText = params.value
          ? params.value
              .toString()
              .match(/.{1,57}/g)
              .join('\n')
          : ''

        return <div style={{ whiteSpace: 'pre-wrap' }}>{splitText}</div>
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },
    {
      field: 'role',
      headerName: translation['label_authority'],
      editable: false,
      flex: 1,
      renderCell: params => {
        // biome-ignore lint/suspicious/noDoubleEquals: <explanation>
        if (params.value == '3')
          // RHQ Member、Kitting operator
          return <div>{translation['label_authority_general']}</div>
        // biome-ignore lint/suspicious/noDoubleEquals: <explanation>
        if (params.value == '2')
          // RHQ Administrator
          return <div>{translation['label_authority_admin']}</div>

        return <div>{translation['label_company_name']}</div>
      },
      sortComparator: ragistrationDateSortComparator,
      sortingOrder: ['asc', 'desc'],
    },

    {
      field: 'registration_date',
      headerName: translation['label_registration_date'],
      editable: false,
      flex: 1,
      renderCell: params => {
        return <div>{conversionTime(params.value)}</div>
      },
      sortComparator: (v1, v2) => new Date(v1).getTime() - new Date(v2).getTime(),
      sortingOrder: ['asc', 'desc'],
    },
  ]

  const theme = createTheme(
    {
      components: {
        MuiDataGrid: {
          styleOverrides: {
            root: {
              border: 'none',
            },
            columnHeader: {
              borderBottom: '2px solid #000',
              '&:focus-within': {
                outline: 'none', // クリック時のヘッダー部の青枠を非表示
              },
            },
            columnHeaderTitle: {
              fontWeight: 'bold',
            },
            cell: {
              '&:focus-within': {
                outline: 'none', // クリック時のデータ部の青枠を非表示
              },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              '&.Mui-checked': {
                color: '#282828',
              },
            },
          },
        },
      },
    },
    enUS,
  )

  return (
    <div style={{ height: 500, width: '100%' }}>
      <ThemeProvider theme={theme}>
        <DataGrid
          checkboxSelection
          disableRowSelectionOnClick={auth.isDisable}
          rows={props.items}
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
          onRowSelectionModelChange={props.handleSelectionModelChange}
          rowSelectionModel={props.gridSelection}
          sx={{
            '& .MuiDataGrid-columnSeparator': {
              visibility: 'visible',
            },
            '& .muidatagrid-root': {
              border: 'none',
            },
            height: 'calc(100vh - 310px)',
          }}
          // hideFooter
          disableColumnMenu
          localeText={{
            ...enUS.components.MuiDataGrid.defaultProps.localeText,
            noRowsLabel: '',
            MuiTablePagination: {
              labelRowsPerPage: translation['label_rows_per_page'],
              labelDisplayedRows: ({ from, to, count }) =>
                (translation['label_display_separator'] ?? '')
                  .replace('{{item}}', `${from}\u2013${to}`)
                  .replace('{{item2}}', String(count)),
            },
          }}
        />
      </ThemeProvider>
    </div>
  )
}
